import { Box, IconButton, InputAdornment, Stack, TextField, Typography, Grid } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { formatTimestampToDate24Hours } from 'utilities/common';
import AngleRightIcon from 'common/customIcons/angle_right';
import SearchIcon from 'common/customIcons/search';
import { CloseRounded } from '@mui/icons-material';
import { useDebounce } from 'hooks/useDebounce';
import { openSnackbar } from '../../../../redux/slices/common';
import { fetchShopSlotLogs } from '../../../../redux/slices/hyperwolf/settings';
import { convertTimeRange } from 'utilities/common';


export default function ShopTimingLogs() {
    const dispatch = useDispatch();
    const { logsloading: loading, slotsLogs = {}, isLoading } = useSelector(state => state.hyperwolfSettings);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const gridRefApi = useRef(null);
    const pageRef = useRef(0);
    const navigate = useNavigate()
    const { memberId } = useParams();
    const [searchTerm, setSearchTerm] = useState("");

    const columnDefs = [
        {
            headerName: 'Day',
            field: 'newChanges',
            suppressMenu: false,
            key: 'newChanges',
            flex: 1.25,
            minWidth: 150,
            cellRenderer: useCallback((props) => (
                props?.data?.newChanges?.day || props?.data?.previousChanges?.day ?
                    props?.data?.newChanges?.day || props?.data?.previousChanges?.day
                    : '-'
            ), []),
            sortable: false,

        },
        {
            headerName: 'Action Type',
            field: 'newChanges',
            key: 'fullName',
            suppressMenu: false,
            cellRenderer: useCallback((props) => (
                props?.value?.actionType ? props?.value?.actionType : '-'

            ), []),
            flex: 1.25,
            minWidth: 150,
            sortable: false
        },
        {
            headerName: 'Region Name',
            field: 'newChanges',
            suppressMenu: false,
            cellRenderer: useCallback((props) => (
                props?.value?.regionName ? props?.value?.regionName : '-'

            ), []),
            flex: 1.25,
            minWidth: 150,
            sortable: false,
        },
        {
            headerName: 'Field Changed',
            field: 'newChanges',
            suppressMenu: false,
            cellRenderer: useCallback((props) => (
                props?.value?.fieldChanged ? props?.value?.fieldChanged : '-'

            ), []),
            flex: 1.25,
            minWidth: 150,
            sortable: false,
        },
        {
            headerName: 'Old Value',
            field: 'previousChanges',
            suppressMenu: false,
            cellRenderer: useCallback((props) => (
                props?.value?.oldValue ? convertTimeRange(props?.value?.oldValue) : '-'
            ), []),
            flex: 1,
            minWidth: 140,
            sortable: false,
        },
        {
            headerName: 'New Value',
            field: 'newChanges',
            suppressMenu: false,
            cellRenderer: useCallback((props) => (
                props?.value?.newValue ? convertTimeRange(props?.value?.newValue) : "-"
            ), []),
            flex: 1,
            minWidth: 140,
            sortable: false,
        },
        {
            headerName: 'User',
            field: 'updatedBy',
            flex: 1,
            minWidth: 140,
            cellRenderer: (props) => {
                return props?.value ? `${props?.value}` : '-';
            },
            sortable: false
        },

        {
            headerName: 'Time',
            field: 'createdDate',
            // headerClass: 'right-aligned-cell-header',
            flex: 1.25,
            minWidth: 150,
            cellRenderer: useCallback((props) => (
                props?.value ? formatTimestampToDate24Hours(props?.value) : '-'
            ), []),
            sortable: false,
            cellStyle: { textAlign: 'left', justifyContent: 'start' },
        },

    ];

    const dataHandler = () => {
        searchFn()
    }
    const getRows = async (params) => {
        gridRefApi.current = params;
        dispatch(fetchShopSlotLogs({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current })).unwrap().then(res => {
            const { activityLogs, total } = res;
            params.successCallback([...activityLogs], total);
            pageRef.current = pageRef.current + 20;
        }).catch(() => { params.successCallback([], 0); });
    };

    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
        // Debounce the search callback
    };

    const handleSearch = useDebounce(() => {
        pageRef.current = 0;
        searchFn();
    }, 500);

    const searchFn = (value) => {
        const dataSource = {
            getRows: (params) => getRows(params, value) // Pass the current filter payload
        };
        if (mainGridApiRef.current) {
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption("datasource", dataSource);
        }
    };

    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (slotsLogs?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [slotsLogs, loading]);



    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };

    return (
        <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }}>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="stretch" gap={2} mb={2}>
                <span onClick={() => navigate(-1)} className='cur-pointer'>
                    <Box display="flex" gap={1} alignItems="center" color='text.main' lineHeight={1}>
                        <AngleRightIcon fontSize="14px" />
                        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                            Delivery Hours
                        </Typography>
                    </Box>
                </span>
            </Stack>

            <Box p={{ xs: 0 }}>
                <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant='h4'>Activity Logs</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                            <TextField
                                className="custom-search-field"
                                placeholder="Search"
                                label=""
                                value={searchTerm}
                                onChange={(e) => searchHandler(e.target.value)}
                                sx={{ minWidth: "280px" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton size="small">
                                                <SearchIcon width="18px" height="18px" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    endAdornment:
                                        searchTerm?.length > 0 ? (
                                            <InputAdornment>
                                                <IconButton onClick={() => searchHandler("")} size="small">
                                                    <CloseRounded />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                }}
                                style={{ minWidth: "300px" }}
                            />

                        </Stack>
                    </Grid>
                </Grid>
            </Box>

            <div className="last-child-left table-nodata-siz">
                <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
            </div>

        </Box>
    );
}